import { Component, OnInit, OnDestroy } from '@angular/core';
import { DevImpersonationService, ImpersonationToken } from '../../_services/dev-impersonation.service';
import { DevRoleSwitcherAuthService } from '../../_services/dev-role-switcher-auth.service';
import { DevImpersonationHttpService } from '../../_services/dev-impersonation-http.service';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserSettingsHttpService } from '../../user-settings/_services/user-settings-http.service';
import { SelectOption } from '../../../views/partials/content/general/searchable-select/searchable-select.component';
import { formatRoleLabel, isCampusScopedUiRole, toPersistedRoleAndPortalView, toUiRole } from '../../_config/portal-view';

type SwitcherRole = 'sadmin' | 'distiadmin' | 'marketadmin' | 'clientadmin' | 'campusadmin' | 'retailadmin';

interface Distributor {
	disti_id: number;
	name: string;
}

interface Client {
	client_id: number;
	company_name: string;
}

interface Campus {
	campus_id: number;
	name: string;
}

interface Market {
	market_id: number;
	market_name: string;
}

@Component({
	selector: 'kt-dev-role-switcher',
	templateUrl: './dev-role-switcher.component.html',
	styleUrls: ['./dev-role-switcher.component.scss']
})
export class DevRoleSwitcherComponent implements OnInit, OnDestroy {
	isExpanded = false;
	currentToken: ImpersonationToken | null = null;
	selectedRole: SwitcherRole | null = null;
	selectedDistiId: string | null = null;
	selectedMarketId: string | null = null;
	selectedClientId: string | null = null;
	selectedCampusId: string | null = null;

	distributors: SelectOption[] = [];
	markets: SelectOption[] = [];
	clients: SelectOption[] = [];
	campuses: SelectOption[] = [];

	loadingDistributors = false;
	loadingMarkets = false;
	loadingClients = false;
	loadingCampuses = false;
	loadingImpersonation = false;

	private subscriptions: Subscription[] = [];

	roles = [
		{ value: 'sadmin', label: 'Super Admin' },
		{ value: 'distiadmin', label: 'Partner Admin' },
		{ value: 'marketadmin', label: 'Market Admin' },
		{ value: 'clientadmin', label: 'Client Admin' },
		{ value: 'campusadmin', label: 'Campus Admin' },
		{ value: 'retailadmin', label: 'Retail Admin' }
	];

	constructor(
		public impersonationService: DevImpersonationService,
		private userSettingsHttp: UserSettingsHttpService,
		private devSwitcherAuthService: DevRoleSwitcherAuthService,
		private impersonationHttp: DevImpersonationHttpService
	) {}

	getRoleLabel(role: string, portalView?: string): string {
		return formatRoleLabel(role, portalView);
	}

	isCampusScopedRole(role: string | null): boolean {
		return isCampusScopedUiRole(role);
	}

	getTriggerText(): string {
		if (!this.currentToken || !this.currentToken.role) {
			return 'Dev Mode';
		}

		const roleLabel = this.getRoleLabel(this.currentToken.role, this.currentToken.portalView);
		let text = `Role: ${roleLabel}`;

		// Add selected entity info
		if (this.currentToken.distiId) {
			const disti = this.distributors.find(d => d.value === this.currentToken.distiId);
			if (disti) {
				text += ` | ${disti.label}`;
			} else {
				text += ` | Disti: ${this.currentToken.distiId}`;
			}
		}

		if (this.currentToken.marketId) {
			const market = this.markets.find(m => m.value === this.currentToken.marketId);
			if (market) {
				text += ` | ${market.label}`;
			} else {
				text += ` | Market: ${this.currentToken.marketId}`;
			}
		}

		if (this.currentToken.clientId) {
			const client = this.clients.find(c => c.value === this.currentToken.clientId);
			if (client) {
				text += ` | ${client.label}`;
			} else {
				text += ` | Client: ${this.currentToken.clientId}`;
			}
		}

		if (this.currentToken.campusId) {
			const campus = this.campuses.find(c => c.value === this.currentToken.campusId);
			if (campus) {
				text += ` | ${campus.label}`;
			} else {
				text += ` | Campus: ${this.currentToken.campusId}`;
			}
		}

		return text;
	}

	ngOnInit(): void {
		if (!this.impersonationService.isDevMode()) {
			return;
		}

		// Subscribe to token changes
		const sub = this.impersonationService.token$.subscribe(token => {
			this.currentToken = token;
			this.resetSelectionsToCurrentToken();
			// Don't load options here - only load when component is expanded and user needs to select
		});
		this.subscriptions.push(sub);

		// Load initial token state
		this.currentToken = this.impersonationService.getToken();
		this.resetSelectionsToCurrentToken();
		// Don't load options here - only load when component is expanded and user needs to select
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(sub => sub.unsubscribe());
	}

	toggleExpanded(): void {
		const wasExpanded = this.isExpanded;
		this.isExpanded = !this.isExpanded;

		if (this.isExpanded) {
			// Reset selections to current token when opening (discard unsaved changes)
			this.resetSelectionsToCurrentToken();

			// Load options only when component is expanded and user can actually select
			// Load distributors if needed for distributor admin or market admin
			if ((this.selectedRole === 'distiadmin' || this.selectedRole === 'marketadmin') && this.distributors.length === 0) {
				this.loadDistributors();
			}
			// Load markets if distributor is selected and role is marketadmin
			if (this.selectedRole === 'marketadmin' && this.selectedDistiId && this.markets.length === 0) {
				this.loadMarkets();
			}
			// Load clients if a distributor is selected and we need to show client options (not for marketadmin)
			if (this.selectedDistiId && this.selectedRole !== 'marketadmin' && this.clients.length === 0) {
				this.loadClients();
			}
			// Load clients if market is selected and role is marketadmin
			if (this.selectedRole === 'marketadmin' && this.selectedMarketId && this.clients.length === 0) {
				this.loadClients();
			}
			// Load clients if role is clientadmin and we need to show client options
			if (this.selectedRole === 'clientadmin' && this.clients.length === 0) {
				this.loadClients();
			}
			// Load campuses if a client is selected and we need to show campus options
			if (this.selectedClientId && this.campuses.length === 0) {
				this.loadCampuses();
			}
			// Load campuses if role is campusadmin and we need to show campus options
			if (isCampusScopedUiRole(this.selectedRole) && this.campuses.length === 0) {
				this.loadCampuses();
			}
		} else if (wasExpanded) {
			// If closing without saving, reset to current token state
			this.resetSelectionsToCurrentToken();
		}
	}

	/**
	 * Reset selections to match current token state
	 */
	private resetSelectionsToCurrentToken(): void {
		if (this.currentToken) {
			this.selectedRole = toUiRole(this.currentToken.role, this.currentToken.portalView) as SwitcherRole;
			this.selectedDistiId = this.currentToken.distiId || null;
			this.selectedMarketId = this.currentToken.marketId || null;
			this.selectedClientId = this.currentToken.clientId || null;
			this.selectedCampusId = this.currentToken.campusId || null;
		} else {
			this.selectedRole = null;
			this.selectedDistiId = null;
			this.selectedMarketId = null;
			this.selectedClientId = null;
			this.selectedCampusId = null;
		}
	}

	onRoleChange(role: string): void {
		this.selectedRole = role as SwitcherRole | null;
		this.selectedDistiId = null;
		this.selectedMarketId = null;
		this.selectedClientId = null;
		this.selectedCampusId = null;
		this.markets = [];
		this.clients = [];
		this.campuses = [];

		// Load relevant options based on role
		if (role === 'distiadmin') {
			// Load distributors for distributor admin
			if (this.distributors.length === 0) {
				this.loadDistributors();
			}
		} else if (role === 'marketadmin') {
			// For marketadmin, load distributors first, then markets
			if (this.distributors.length === 0) {
				this.loadDistributors();
			}
		} else if (role === 'clientadmin') {
			// For clientadmin, load all clients (no distributor filter needed)
			this.loadClients();
		} else if (isCampusScopedUiRole(role)) {
			this.loadCampuses();
		}
		// Super admin doesn't need any additional selections

		// Don't apply override immediately - wait for save
	}

	onDistiChange(distiId: string | null): void {
		this.selectedDistiId = distiId;
		this.selectedMarketId = null;
		this.selectedClientId = null;
		this.selectedCampusId = null;
		this.markets = [];
		this.clients = [];
		this.campuses = [];

		if (distiId) {
			if (this.selectedRole === 'marketadmin') {
				this.loadMarkets();
			} else {
				this.loadClients();
			}
		}

		// Don't apply override immediately - wait for save
	}

	onMarketChange(marketId: string | null): void {
		this.selectedMarketId = marketId;
		this.selectedClientId = null;
		this.selectedCampusId = null;
		this.clients = [];
		this.campuses = [];

		if (marketId) {
			this.loadClients();
		}

		// Don't apply override immediately - wait for save
	}

	onClientChange(clientId: string | null): void {
		this.selectedClientId = clientId;
		this.selectedCampusId = null;
		this.campuses = [];

		if (clientId) {
			this.loadCampuses();
		}

		// Don't apply override immediately - wait for save
	}

	onCampusChange(campusId: string | null): void {
		this.selectedCampusId = campusId;
		// Don't apply override immediately - wait for save
	}

	clearOverride(): void {
		// Clear impersonation token (which clears everything)
		this.impersonationService.clearToken();
		this.resetSelectionsToCurrentToken();
		this.clients = [];
		this.campuses = [];
		this.distributors = [];
		// Keep panel open after reset so user can select a new role if needed
	}

	saveAndClose(): void {
		if (!this.selectedRole) {
			// If no role selected, clear override and impersonation
			this.clearOverride();
			this.isExpanded = false;
			// Reload the page
			window.location.reload();
			return;
		}

		// Request impersonation from backend
		this.loadingImpersonation = true;
		this.requestImpersonation();
	}

	private requestImpersonation(): void {
		const persisted = toPersistedRoleAndPortalView(this.selectedRole);
		const request = {
			role: persisted.role as 'sadmin' | 'distiadmin' | 'marketadmin' | 'clientadmin' | 'campusadmin',
			distiId: this.selectedDistiId || null,
			marketId: this.selectedMarketId || null,
			clientId: this.selectedClientId || null,
			campusId: this.selectedCampusId || null,
			portalView: persisted.portal_view
		};

		const sub = this.impersonationHttp.impersonate(request).subscribe(
			(tokenData) => {
				// Store the impersonation token
				// DevImpersonationService will automatically update permissions
				this.impersonationService.setToken(tokenData);

				this.loadingImpersonation = false;
				this.isExpanded = false;

				// Reload the page to apply the changes
				window.location.reload();
			},
			(error) => {
				console.error('Failed to impersonate:', error);
				this.loadingImpersonation = false;
				// Show error to user (you may want to add a toast/notification here)
				alert('Failed to start impersonation. Please check console for details.');
			}
		);

		this.subscriptions.push(sub);
	}

		// Removed applyOverride() - no longer needed since DevImpersonationService
	// now handles permissions automatically when token is set

	private loadDistributors(): void {
		this.loadingDistributors = true;
		const sub = this.userSettingsHttp.getDistis().pipe(
			map((distis: Distributor[]) => {
				return distis.map((d: Distributor) => ({
					value: String(d.disti_id),
					label: d.name,
					...d
				}));
			})
		).subscribe(
			distis => {
				this.distributors = distis;
				this.loadingDistributors = false;
			},
			error => {
				this.loadingDistributors = false;
			}
		);
		this.subscriptions.push(sub);
	}

	private loadMarkets(): void {
		if (!this.selectedDistiId) {
			return;
		}

		this.loadingMarkets = true;
		const params: { disti_id?: string } = {};

		if (this.selectedDistiId) {
			params.disti_id = this.selectedDistiId;
		}

		const sub = this.userSettingsHttp.getMarkets(params).pipe(
			map((markets: Market[]) => {
				return markets.map((m: Market) => ({
					value: String(m.market_id),
					label: m.market_name,
					...m
				}));
			})
		).subscribe(
			markets => {
				this.markets = markets;
				this.loadingMarkets = false;
			},
			error => {
				this.loadingMarkets = false;
			}
		);
		this.subscriptions.push(sub);
	}

	private loadClients(): void {
		// Allow loading clients even without distributor for clientadmin and campusadmin roles
		if (!this.selectedDistiId && this.selectedRole !== 'clientadmin' && !isCampusScopedUiRole(this.selectedRole) && this.selectedRole !== 'marketadmin') {
			return;
		}

		this.loadingClients = true;
		const params: { disti_id?: string; market_id?: string } = {};

		if (this.selectedDistiId) {
			params.disti_id = this.selectedDistiId;
		}

		if (this.selectedMarketId) {
			params.market_id = this.selectedMarketId;
		}

		const sub = this.userSettingsHttp.getClients(params).pipe(
			map((clients: Client[]) => {
				return clients.map((c: Client) => ({
					value: String(c.client_id),
					label: c.company_name,
					...c
				}));
			})
		).subscribe(
			clients => {
				this.clients = clients;
				this.loadingClients = false;
			},
			error => {
				this.loadingClients = false;
			}
		);
		this.subscriptions.push(sub);
	}

	private loadCampuses(): void {
		// For campusadmin, allow loading without client_id
		// For other roles, require client_id
		if (!this.selectedClientId && !isCampusScopedUiRole(this.selectedRole)) {
			return;
		}

		this.loadingCampuses = true;
		const params: { client_id?: string; disti_id?: string } = {};

		// Only add client_id if it's selected (not required for campusadmin to see all)
		if (this.selectedClientId) {
			params.client_id = this.selectedClientId;
		}

		if (this.selectedDistiId) {
			params.disti_id = this.selectedDistiId;
		}

		const sub = this.userSettingsHttp.getCampus(params).pipe(
			map((campuses: Campus[]) => {
				return campuses.map((c: Campus) => ({
					value: String(c.campus_id),
					label: c.name,
					...c
				}));
			})
		).subscribe(
			campuses => {
				this.campuses = campuses;
				this.loadingCampuses = false;
			},
			error => {
				this.loadingCampuses = false;
			}
		);
		this.subscriptions.push(sub);
	}

	/**
	 * Handle close dev mode button click - unauthenticates user
	 */
	onCloseDevMode(): void {
		// Clear authentication state
		this.devSwitcherAuthService.setAuthenticated(false);
	}
}

